import type { Dot } from '@hillchart/shared';

/**
 * Pure math helpers for the hill chart — no DOM, no React, no SVG strings
 * with side effects. Anything that takes a hill position (`x ∈ [0, 100]`)
 * or an SVG coordinate lives here so the `<Hill />` component stays focused
 * on rendering and pointer wiring.
 *
 * Coordinate systems:
 *   - Hill position `x` ∈ [0, 100]        (the domain the user cares about)
 *   - SVG x / y    ∈ [0, width] / [0, height]
 *     (SVG y grows downward — baseline is *large* y, peak is *small* y)
 *
 * The mapping between them is linear on x and sinusoidal on y:
 *   svgX = paddingX + (x / 100) · usableWidth
 *   svgY = baselineY − amplitude · sin(π · x / 100)
 */

/**
 * Fixed SVG viewBox dimensions. Chosen so a 16:9 container displays the
 * chart with comfortable padding around the curve. Kept as a `const`
 * assertion so consumers can destructure with type narrowing.
 *
 * `paddingX` must be wide enough to fit a dot at x=0 or x=100 without
 * clipping; `paddingTop` gives headroom for labels above the peak;
 * `paddingBottom` leaves room for the "figuring it out" / "done" legend.
 */
export const HILL_VIEWBOX = {
  width: 1000,
  height: 400,
  paddingX: 48,
  paddingTop: 40,
  paddingBottom: 64,
} as const;

/**
 * Peak height of the sinusoid above the baseline. Derived from the viewBox
 * so a tweak to the padding values propagates automatically.
 */
export const HILL_AMPLITUDE =
  HILL_VIEWBOX.height - HILL_VIEWBOX.paddingTop - HILL_VIEWBOX.paddingBottom;

/**
 * SVG y-coordinate of the curve's baseline — where x=0 and x=100 land.
 * Remember: larger y = lower on screen, so this is a *large* number.
 */
export const HILL_BASELINE_Y = HILL_VIEWBOX.height - HILL_VIEWBOX.paddingBottom;

/** Horizontal span available for the curve, once padding is taken out. */
const USABLE_WIDTH = HILL_VIEWBOX.width - 2 * HILL_VIEWBOX.paddingX;

/**
 * Map a hill position `x ∈ [0, 100]` to an SVG x-coordinate.
 *
 * No clamping: the caller may want to place something just past the edge
 * (e.g. a label). Use `clampHillX` first if bounds are required.
 */
export function hillXToSvgX(x: number): number {
  return HILL_VIEWBOX.paddingX + (x / 100) * USABLE_WIDTH;
}

/**
 * Inverse of `hillXToSvgX`. Useful during drag — convert the pointer's
 * SVG-space x back into hill position, then clamp. The pointer can
 * overshoot the viewBox while dragging, so callers should always chain
 * `clampHillX` before persisting.
 */
export function svgXToHillX(svgX: number): number {
  return ((svgX - HILL_VIEWBOX.paddingX) / USABLE_WIDTH) * 100;
}

/**
 * SVG y-coordinate of the sinusoidal curve at hill position `x`.
 *   y = baseline − amplitude · sin(π · x / 100)
 *   x=0   → baseline      (left foot)
 *   x=50  → baseline − amplitude  (peak, "crest")
 *   x=100 → baseline      (right foot, "done")
 */
export function hillYFromX(x: number): number {
  return HILL_BASELINE_Y - HILL_AMPLITUDE * Math.sin((Math.PI * x) / 100);
}

/**
 * Clamp a hill position into `[0, 100]`. The shared `hillPositionSchema`
 * only accepts values in that range, so any overshoot from a drag must be
 * clamped before the PATCH is sent — otherwise the backend rejects with
 * a Zod error and the optimistic update gets rolled back for no UX reason.
 */
export function clampHillX(x: number): number {
  if (x < 0) return 0;
  if (x > 100) return 100;
  return x;
}

/**
 * Build the SVG `d` attribute for the hill curve as a polyline sampled at
 * 2-unit steps (51 points, 50 segments). A cubic Bézier approximation
 * would render identically at this resolution but requires hand-tuned
 * control points; sampling the exact sinusoid is correct-by-construction
 * and drops out of the same math as `hillYFromX`, so there's one source
 * of truth between "where is the curve" and "where is the dot".
 *
 * 2-unit steps are a deliberate trade — finer than the eye can resolve at
 * 1000px width, coarser than the 100-point path you get from step=1.
 */
export function buildHillPath(): string {
  const segments: string[] = [];
  for (let x = 0; x <= 100; x += 2) {
    const svgX = hillXToSvgX(x).toFixed(2);
    const svgY = hillYFromX(x).toFixed(2);
    segments.push(`${x === 0 ? 'M' : 'L'} ${svgX} ${svgY}`);
  }
  return segments.join(' ');
}

/**
 * Hill-units window inside which two dots are considered "at the same
 * position" and stacked vertically rather than rendered on top of each
 * other. 2 ≈ one dot diameter at the production radius (12) when mapped
 * back through `hillXToSvgX` — close enough that the eye can no longer
 * separate them without help.
 *
 * Used by `clusterDots`. Tuning higher than ~3 starts to merge dots that
 * the user *could* still tell apart visually, which feels dishonest about
 * their actual progress; tuning lower than 1 lets near-identical dots
 * still overlap. 2 is the sweet spot we landed on.
 */
export const CLUSTER_TOLERANCE = 2;

/**
 * Vertical separation (in SVG units) between two adjacent tiers in a
 * cluster. Sized so a 2× radius dot (production: 12 ⇒ 24 SVG units of
 * diameter) leaves a thin margin at this spacing, without the pile
 * growing taller than necessary.
 */
export const TIER_SPACING_SVG = 28;

/**
 * Above this hill x, inline labels render to the LEFT of their dot rather
 * than the right, so the text doesn't run past the chart's right padding
 * and clip out of the viewBox.
 *
 * Calibrated empirically: at the production viewBox (width 1000,
 * paddingX 48), a dot at x=70 sits at SVG x ≈ 681 — leaving ~271 SVG
 * units of headroom to the right edge, comfortably more than any
 * realistic milestone name renders to. Anything above 70 starts to risk
 * a long name running into or past the right padding, so we flip.
 *
 * Below the threshold we keep the right-side rendering — left-aligned
 * labels next to a dot at x=20 would overlap with the next dot to the
 * right far more often than they help.
 */
export const LABEL_FLIP_THRESHOLD_X = 70;

/**
 * One dot's place in the (potential) vertical pile at its hill position.
 * `tier === 0` is the dot that sits exactly on the curve; `tier === 1`
 * stacks one slot above, etc. `clusterSize` is the total number of dots
 * sharing this position (≥ 1) — needed by `tierYOffset` to apply the
 * dynamic compression when a tall cluster would otherwise clip the top
 * of the viewBox.
 */
export interface ClusterInfo {
  /** Index within the cluster, 0-based. 0 = on the curve. */
  tier: number;
  /** How many dots are in this cluster (1 = solo). */
  clusterSize: number;
}

/**
 * Group dots that share a hill position within `tolerance` units, and
 * assign each one a stable tier within its cluster.
 *
 * Algorithm:
 *   1. Sort dots by `(x asc, id asc)`. The id tiebreaker pins tier
 *      assignment across re-renders: two dots at the same x always end
 *      up in the same tier order, so a teammate's drag elsewhere in the
 *      chart doesn't reshuffle the entire pile (which would make the
 *      animation ugly and the legend numbers feel unstable).
 *   2. Walk left-to-right. Each cluster keeps the x of its FIRST dot as
 *      its anchor; the next dot opens a new cluster only if its x
 *      exceeds `anchorX + tolerance`. Comparing to the *anchor* (not the
 *      previous dot) prevents transitive chaining: dots at x=0, 2, 4, 6
 *      with tolerance 2 end up as four separate clusters of one rather
 *      than one cluster of four.
 *   3. Tier within a cluster = position in the sorted walk (0, 1, 2, …).
 *
 * Returns a Map keyed by `dot.id` so callers can look tier/clusterSize up
 * in O(1) during render without re-sorting.
 *
 * Tolerance is in HILL UNITS (0–100), not SVG pixels.
 */
export function clusterDots(dots: readonly Dot[], tolerance: number): Map<string, ClusterInfo> {
  // Sort copy — never mutate the caller's array, since `hill.dots` comes
  // straight from the TanStack Query cache.
  const sorted = [...dots].sort((a, b) => {
    if (a.x !== b.x) return a.x - b.x;
    return a.id.localeCompare(b.id);
  });

  // Two-pass: first bucket dots into clusters (we only know the final
  // cluster size after walking past the last member), then write
  // tier + clusterSize back. The alternative — back-patching `clusterSize`
  // as the cluster grows — is fragile because `Map` set order matters
  // for nothing here, but the patch loop adds noise without saving work.
  const buckets: string[][] = [];
  let anchorX: number | null = null;
  let current: string[] | null = null;
  for (const dot of sorted) {
    if (anchorX === null || dot.x - anchorX > tolerance) {
      current = [];
      buckets.push(current);
      anchorX = dot.x;
    }
    // `current` is guaranteed non-null after the if-branch; the optional
    // chain is just to satisfy TS's narrowing without a non-null
    // assertion (banned by Biome).
    current?.push(dot.id);
  }

  const result = new Map<string, ClusterInfo>();
  for (const bucket of buckets) {
    const clusterSize = bucket.length;
    bucket.forEach((id, tier) => {
      result.set(id, { tier, clusterSize });
    });
  }
  return result;
}

/**
 * SVG-unit offset to subtract from a dot's `baseY` (= `hillYFromX(x)`)
 * to place it at its tier within the cluster. Tier 0 always returns 0 —
 * the lead dot of a cluster sits exactly on the curve.
 *
 * Dynamic compression: a cluster of N dots needs `(N-1) · TIER_SPACING_SVG`
 * of vertical headroom. At the feet of the hill (x near 0 or 100) `baseY`
 * is at the baseline so headroom is the full chart height — no
 * compression. Near the crest (x ≈ 50) `baseY` is small (the curve is
 * high), so the same fan would push the top dots above the viewBox. We
 * detect that case and shrink the spacing evenly to fit.
 *
 * The compression keeps the cluster legible even at the peak: dots stay
 * inside the chart, although they crowd each other more. For the
 * common case (cluster at x=0 / x=100, where hill charts naturally
 * bunch up), no compression ever fires.
 */
export function tierYOffset(tier: number, clusterSize: number, baseY: number): number {
  if (tier === 0 || clusterSize <= 1) return 0;

  const headroom = baseY - HILL_VIEWBOX.paddingTop;
  const neededFullFan = (clusterSize - 1) * TIER_SPACING_SVG;
  // Compression only kicks in if the full fan wouldn't fit. Negative or
  // zero headroom (would mean the curve already sits at/above paddingTop,
  // i.e. the dot is at the crest) collapses spacing to 0 — every tier
  // ends up on top of the lead dot, which at least keeps everything in
  // the viewBox while clearly signalling "this many dots here".
  const spacing =
    neededFullFan > headroom ? Math.max(0, headroom / (clusterSize - 1)) : TIER_SPACING_SVG;
  return tier * spacing;
}
